class ProductComparisonTable {
  constructor() {
    this.container = document.querySelector('.product-comparison-table');
    if (!this.container) return;

    this.productsContainer = this.container.querySelector('.product-comparison-table__products');
    this.swiperElement = this.container.querySelector('.product-comparison-table__swiper');
    this.swiper = null;

    // Safely parse section settings
    try {
      this.settings = JSON.parse(this.container.dataset.sectionSettings || '{}');
    } catch (error) {
      console.error('Error parsing section settings:', error);
      console.log('Raw settings:', this.container.dataset.sectionSettings);
      this.settings = {};
    }

    this.init();
  }

  init() {
    // Set up event listeners
    this.setupEventListeners();

    // Initialize Swiper after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initializeSwiper();
    }, 100);

    // Add resize listener to recalculate navigation visibility
    this.setupResizeListener();

    // Load products from query string if enabled
    if (this.settings.use_query_string) {
      this.loadQueryStringProducts();
    }
  }

  initializeSwiper() {
    // Get the swiper instance that was created by the component-swiper-slider.js
    // We need to wait for it to be initialized
    const checkSwiper = () => {
      if (this.swiperElement && this.swiperElement.swiper) {
        this.swiper = this.swiperElement.swiper;
        return true;
      }
      return false;
    };

    // Try to get the swiper instance, with fallback
    if (!checkSwiper()) {
      // If swiper isn't ready yet, wait a bit more
      setTimeout(() => {
        if (!checkSwiper()) {
          console.warn('Swiper not found for product comparison table');
        }
      }, 500);
    }
  }

  // Set up resize listener to recalculate navigation on screen size changes
  setupResizeListener() {
    let resizeTimeout;

    window.addEventListener('resize', () => {
      // Debounce the resize event to avoid excessive calculations
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateNavigationVisibility();
      }, 150);
    });
  }

  // Method to update navigation visibility after dynamic changes
  updateNavigationVisibility() {
    if (!this.swiper || !this.swiperElement) return;

    // Let the component-swiper-slider.js handle navigation visibility
    // We only need to handle the special case of resetting position when navigation is not needed
    const slides = this.swiperElement.querySelectorAll('.swiper-slide');
    const slideCount = slides.length;

    // Get current slides per view based on screen size
    let currentSlidesPerView;
    const screenWidth = window.innerWidth;

    if (screenWidth >= 1440) {
      currentSlidesPerView = 6;
    } else if (screenWidth >= 990) {
      currentSlidesPerView = 4;
    } else if (screenWidth >= 768) {
      currentSlidesPerView = 3;
    } else {
      currentSlidesPerView = 1.75; // Mobile setting from Swiper config
    }

    // Only reset position when navigation is not needed
    const needsNavigation = slideCount > currentSlidesPerView;
    if (!needsNavigation) {
      this.swiper.setTranslate(0);
    }
  }

  // Set up event listeners for remove buttons and add to cart
  setupEventListeners() {
    // Use event delegation to handle clicks within the products container
    this.productsContainer.addEventListener('click', (event) => {
      // Handle remove button clicks
      if (event.target.closest('.card-product__remove-button')) {
        const productCard = event.target.closest('card-product');
        if (productCard && productCard.classList.contains('card-product--removable')) {
          this.removeProduct(productCard);
        }
      }

      // Handle add to cart button clicks
      if (event.target.closest('.card-product__add-to-cart')) {
        event.preventDefault();
        const addToCartButton = event.target.closest('.card-product__add-to-cart');
        this.addToCart(addToCartButton);
      }
    });
  }

  // Remove a product card from the list
  removeProduct(productCard) {
    const slide = productCard.closest('.swiper-slide');
    if (slide && this.swiper) {
      // Get the slide index
      const slideIndex = Array.from(this.productsContainer.children).indexOf(slide);

      // Remove the slide using Swiper's method
      this.swiper.removeSlide(slideIndex);
    } else {
      // Fallback to regular removal
      productCard.remove();
    }

    // Ensure productCard has a data-product-handle attribute
    const productHandle = productCard.querySelector('.card-product__inner').href.split('/').pop();
    const formattedProductHandle = productHandle.trim().toLowerCase();

    const selectedProducts = JSON.parse(localStorage.getItem('selectedProductsForComparison')) || [];
    const updatedProducts = selectedProducts.filter(item => {
      // If item is an object, access the handle property
      const handle = typeof item === 'string' ? item : item.handle;
      if (!handle) {
        return true; // Keep the item if handle is not found
      }

      const normalizedHandle = handle.trim().toLowerCase();
      return normalizedHandle !== formattedProductHandle;
    });

    localStorage.setItem('selectedProductsForComparison', JSON.stringify(updatedProducts));

    // Update navigation visibility after removing product
    this.updateNavigationVisibility();

    // Check if all products are removed and show empty state
    this.checkEmptyState();
  }

  // Check if all products are removed and show empty state
  checkEmptyState() {
    const slides = this.productsContainer.querySelectorAll('.swiper-slide');
    const hasProducts = slides.length > 0;

    if (!hasProducts) {
      this.showEmptyState();
    } else {
      this.hideEmptyState();
    }
  }

  // Show empty state message
  showEmptyState() {
    // Check if empty state already exists
    if (this.container.querySelector('.product-comparison-table__empty-state')) {
      return;
    }

    const emptyStateHTML = `
      <div class="product-comparison-table__empty-state">
        <h2 class="product-comparison-table__empty-state-title heading--3">No products to compare</h2>
        <p class="product-comparison-table__empty-state-message">You haven't added any products to compare yet.</p>
        <a href="/collections/all" class="button button--primary product-comparison-table__empty-state-button">Continue Shopping</a>
      </div>
    `;

    // Insert empty state after the swiper container
    const swiperContainer = this.container.querySelector('.swiper-container');
    if (swiperContainer) {
      swiperContainer.insertAdjacentHTML('afterend', emptyStateHTML);
    }
  }

  // Hide empty state message
  hideEmptyState() {
    const emptyState = this.container.querySelector('.product-comparison-table__empty-state');
    if (emptyState) {
      emptyState.remove();
    }
  }

  // Add product to cart
  async addToCart(button) {
    const variantId = button.dataset.variantId;
    const productHandle = button.dataset.productHandle;

    if (!variantId) {
      console.error('No variant ID found for add to cart');
      return;
    }

    // Disable button and show loading state
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      // Try different cart endpoints in order of preference
      const cartUrls = [window.routes?.cart_add_url, '/cart/add.js', '/cart/add'].filter(Boolean);

      let success = false;
      let result = null;

      for (const url of cartUrls) {
        try {
          console.log(`Trying cart URL: ${url}`);

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
            }),
          });

          console.log(`Response status: ${response.status}`);
          console.log(`Response headers:`, response.headers);

          if (response.ok) {
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
              result = await response.json();
              success = true;
              break;
            } else {
              // If it's not JSON, it might still be successful (some themes return HTML)
              const text = await response.text();
              console.log(`Non-JSON response: ${text.substring(0, 100)}`);

              // Check if it looks like a success response
              if (response.status === 200 || response.status === 302) {
                success = true;
                break;
              }
            }
          }
        } catch (urlError) {
          console.log(`Failed with ${url}:`, urlError);
          continue;
        }
      }

      if (!success) {
        throw new Error('All cart endpoints failed');
      }

      // Show success state
      button.textContent = 'Added!';
      button.disabled = false;

      // Trigger cart update events
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:build'));

      // Also try to update cart count if element exists
      const cartCount = document.querySelector('.cart-count-bubble, [data-cart-count], .cart-count');
      if (cartCount) {
        const currentCount = parseInt(cartCount.textContent) || 0;
        cartCount.textContent = currentCount + 1;
      }

      // Reset button after 2 seconds
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);

      // Show error state
      button.textContent = 'Error';

      // Reset button after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    }
  }

  // Loads products from URL query parameters
  loadQueryStringProducts() {
    const urlParams = new URLSearchParams(window.location.search);
    const queryKey = this.settings.compare_query_string_key_override || this.getThemeCompareKey();
    const delimiter = this.getThemeCompareDelimiter();

    const compareValue = urlParams.get(queryKey);

    if (!compareValue) {
      return;
    }

    const productIdentifiers = compareValue.split(delimiter).filter((id) => id.trim());

    if (productIdentifiers.length === 0) {
      return;
    }

    // Load each product
    this.loadProducts(productIdentifiers);
  }

  getThemeCompareKey() {
    return window.themeSettings?.compareQueryStringKey || 'compare';
  }

  getThemeCompareDelimiter() {
    return window.themeSettings?.compareQueryStringDelimiter || ',';
  }

  // Load products by handle
  async loadProducts(productIdentifiers) {
    try {
      const promises = productIdentifiers.map((id) => this.loadSingleProduct(id));
      await Promise.all(promises);

      // Update navigation visibility after all products are loaded
      this.updateNavigationVisibility();
    } catch (error) {
      console.error('Error loading products from query string:', error);
    }
  }

  //Loads a single product by handle and appends it to the container
  async loadSingleProduct(productHandle) {
    try {
      // Fetch product using the compare-card template
      const response = await fetch(`/products/${productHandle}?view=compare-card`);

      if (!response.ok) {
        console.warn(`Failed to load product ${productHandle}: ${response.status}`);
        return;
      }

      const html = await response.text();
      this.appendProductCard(html);
    } catch (error) {
      console.error(`Error loading product ${productHandle}:`, error);
    }
  }

  // Add product card to the container
  appendProductCard(html) {
    // Create a temporary container to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html.trim();

    // Extract the product card and add removable class
    const productCard = tempDiv.querySelector('card-product');
    if (productCard) {
      productCard.classList.add('card-product--removable');

      // Create a swiper slide wrapper
      const slideWrapper = document.createElement('div');
      slideWrapper.className = 'swiper-slide';
      slideWrapper.appendChild(productCard);

      if (this.swiper) {
        // Add the slide using Swiper's method
        this.swiper.appendSlide(slideWrapper);
      } else {
        // Fallback to regular append
        this.productsContainer.appendChild(slideWrapper);
      }

      this.checkForMetafields(productCard);

      // Update navigation visibility after adding product
      this.updateNavigationVisibility();

      // Hide empty state if it exists
      this.hideEmptyState();

      window.loadStampedBadges?.();
    }
  }

  async checkForMetafields(productCard) {
    const productId = productCard.dataset.productId;
    const metafieldsJSON = this.settings.metafields;
    const metafieldsParts = metafieldsJSON.replace(/\n/g, '|').split('|');

    // Loop through the metafieldsParts and create an array of metafields
    const metafieldsIdentifiers = [];
    metafieldsParts.forEach((part) => {
      if (part.includes('Value:')) {
        const metafieldPath = part.split('Value:')[1].trim();
        const [namespace, key] = metafieldPath.split('.');
        if (namespace && key) {
          metafieldsIdentifiers.push(`{namespace: "${namespace}", key: "${key}"}`);
        }
      }
    });

    if (!productId || metafieldsIdentifiers.length === 0) {
      return;
    }

    try {
      // Query Shopify Storefront API to get product metafields
      const query = `
        query getProductMetafields($id: ID!) {
          product(id: $id) {
            id
            title
            metafields(identifiers: [${metafieldsIdentifiers.join(', ')}]) {
              key
              namespace
              value
            }
          }
        }
      `;

      // Try different API versions to find one that works
      const apiVersions = ['2024-01', '2023-10', '2023-07', '2023-04'];
      let response = null;
      let lastError = null;

      for (const version of apiVersions) {
        try {
          response = await fetch(`/api/${version}/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': '4ea5b6aad258af4d4b056a6af5794571',
            },
            body: JSON.stringify({
              query: query,
              variables: {
                id: `gid://shopify/Product/${productId}`,
              },
            }),
          });

          if (response.ok) {
            console.log(`Success with API version: ${version}`);
            break;
          } else {
            lastError = `Version ${version}: ${response.status} ${response.statusText}`;
            console.warn(`Failed with API version ${version}: ${response.status} ${response.statusText}`);
          }
        } catch (error) {
          lastError = `Version ${version}: ${error.message}`;
          console.warn(`Error with API version ${version}:`, error);
        }
      }

      if (!response || !response.ok) {
        return;
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        return;
      }

      const product = data.data.product;
      if (!product) {
        console.warn(`No product found for ID: ${productId}`);
        return;
      }

      console.log('Product metafields from Storefront API:', product.metafields);

      // Process metafields and update the product card
      this.processProductMetafields(productCard, data);
    } catch (error) {
      console.error(`Error fetching metafields for product ${productId}:`, error);
    }
  }

  processProductMetafields(productCard, data) {
    const specsContainer = productCard.querySelector('.card-product__specs tbody');
    const metafields = data.data.product.metafields;

    // Build a label lookup map from the metafields textarea setting
    const labelMap = {};
    const metafieldsRaw = this.settings.metafields || '';
    metafieldsRaw.split('\n').forEach((line) => {
      line = line.trim();
      if (!line || line.startsWith('#')) return;
      const parts = line.split('|');
      let label = '';
      let key = '';
      parts.forEach((part) => {
        if (part.includes('Label:')) label = part.split('Label:')[1].trim();
        if (part.includes('Value:')) key = part.split('Value:')[1].trim().split('.').pop();
      });
      if (key && label) labelMap[key] = label;
    });

    // Loop through the metafields and add them to the specs container
    metafields.forEach((metafield) => {
      if (metafield !== null) {
        const label = labelMap[metafield.key] || metafield.key.replace(/_/g, ' ');
        const specRow = `<tr>
          <th class="card-product__specs-table-row-heading">${label}:</th>
          <td class="card-product__specs-table-row-cell">${metafield.value}</td>
        </tr>`;

        specsContainer.insertAdjacentHTML('beforeend', specRow);
      }
    });
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductComparisonTable();
});
